import { api } from '../net/api';

export type PushSupport = 'unsupported' | 'denied' | 'default' | 'granted';

const SW_URL = import.meta.env.DEV ? '/src/pwa/sw.ts' : '/sw.js';

/** Register the service worker (offline shell + push). Safe to call repeatedly. */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register(SW_URL, {
      type: import.meta.env.DEV ? 'module' : 'classic',
      scope: '/',
    });
  } catch (error) {
    console.warn('Service worker registration failed', error);
    return null;
  }
}

export function pushSupport(): PushSupport {
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
}

/** iOS only allows web push for installed (home-screen) web apps. */
export function needsInstallForPush(): boolean {
  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const standalone =
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return isIos && !standalone;
}

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalised = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalised);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * Ask for permission (must be called from a user gesture) and register this device for push
 * notifications for the given player token.
 */
export async function enablePush(playerToken: string): Promise<PushSupport> {
  if (pushSupport() === 'unsupported') return 'unsupported';
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission;

  const registration = (await navigator.serviceWorker.getRegistration()) ?? (await registerServiceWorker());
  if (!registration) return 'unsupported';
  const { publicKey } = await api.pushPublicKey();
  const existing = await registration.pushManager.getSubscription();
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }));
  await api.pushSubscribe(playerToken, subscription.toJSON());
  return 'granted';
}

/** Stop push notifications for this player on this device. */
export async function disablePush(playerToken: string): Promise<void> {
  await api.pushUnsubscribe(playerToken);
}
